import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { useAlertEvents, useAlertRules, useDeleteAlertRule, useUpdateAlertRule } from 'src/api/queries';
import { AbsenceState } from 'src/components/editorial/AbsenceState';
import { LoadFailure } from 'src/components/editorial/LoadFailure';
import { Badge } from 'src/components/ui/Badge';
import { Button } from 'src/components/ui/Button';
import { Icon } from 'src/components/ui/Icon';
import { Notice } from 'src/components/ui/Notice';
import { Skeleton } from 'src/components/ui/Skeleton';
import { ALERT_SURFACE_LABEL, buildAlertFigures, formatAlertScopes, ruleMarkIcon, ruleMarkTone, type AlertFigures } from 'src/lib/alerts';
import { markAlertsSeen, readAlertsSeenAt } from 'src/lib/alertsSeen';
import { formatDateTime, relativeDayTime } from 'src/lib/time';
import type { EditorialAlertEvent, EditorialAlertRule } from 'src/types/editorial';

const RECENT_COUNT = 3;

/**
 * Answers: what has the newsroom asked to be told about, and what has that produced.
 *
 * The two halves are in that order because the second is only readable through the first: a feed
 * of matches means nothing until you know which standing questions produced them. Rules are
 * configuration and the list is short by nature, so it is not paginated. The full feed lives on
 * its own page (`/alertas/historial`, Composition-grammar "Split pair"'s right column here is only
 * a preview of it) because it grows with every chunk of every broadcast and deserves its own
 * search rather than crowding this one.
 */
export function AlertsWorkspace() {
  const navigate = useNavigate();
  const rules = useAlertRules(true);
  const events = useAlertEvents({ rowsPerPage: 50 });
  const [search, setSearch] = useState('');

  // Frozen at mount, on purpose: this is the "unread since I last opened this screen" cutoff for
  // the whole visit. The effect below moves the real cursor forward as pages arrive, but the badges
  // on screen should not flip from "por revisar" to "visto" out from under someone still reading.
  const [seenAt] = useState(readAlertsSeenAt);

  // Reading the history is what clears the rail's badge. On every arriving page, not only on
  // mount: the list polls, and an editor sitting on this screen while a broadcast runs is reading
  // the new entries as they land.
  const newestOccurredAt = events.data?.events[0]?.occurredAt;
  useEffect(() => {
    if (newestOccurredAt) markAlertsSeen(Date.parse(newestOccurredAt));
  }, [newestOccurredAt]);

  const visibleRules = filterRules(rules.data?.rules ?? [], search);
  const figures: AlertFigures | null = rules.data && events.data ? buildAlertFigures(rules.data.rules, events.data.events, seenAt, new Date()) : null;

  return (
    <div className="page-hero">
      <header className="landmark alerts__head">
        <div>
          <p className="thread__eyebrow">Centro de monitoreo</p>
          <h1 className="section-title">Alertas</h1>
          <p className="relevance__note">Detecta los cambios importantes sin perderte entre alertas, resultados y configuraciones.</p>
        </div>
        <Link to="/alertas/nueva">
          <Button variant="primary">+ Añadir</Button>
        </Link>
      </header>

      {figures && <AlertStats figures={figures} />}

      <div className="alerts__layout">
        <section className="alerts__panel">
          <header className="alerts__panel-head">
            <div>
              <h2 className="section-title">Alertas configuradas</h2>
              <p className="relevance__note">Administra las alertas que ya estás monitoreando.</p>
            </div>
            {rules.data && rules.data.rules.length > 0 && (
              <div className="alerts__search">
                <label>
                  <span className="visually-hidden">Buscar alertas configuradas</span>
                  <span className="alerts__search-field">
                    <input
                      type="search"
                      value={search}
                      onChange={event => setSearch(event.target.value)}
                      placeholder="Buscar alerta"
                    />
                  </span>
                </label>
              </div>
            )}
          </header>

          <div className="alerts__panel-pad">
            {rules.isPending && <Skeleton variant="rows" count={3} />}
            {rules.isError && <LoadFailure title="No se pudieron cargar las reglas" error={rules.error} onRetry={rules.refetch} />}
            {rules.data && rules.data.rules.length === 0 && (
              <AbsenceState
                kind="empty"
                what="No hay reglas de alerta."
                wouldChange="Crea una y el pipeline empezará a evaluarla en la siguiente transmisión."
                action={{ label: 'Crear la primera', onClick: () => navigate('/alertas/nueva') }}
              />
            )}
            {rules.data && rules.data.rules.length > 0 && visibleRules.length === 0 && (
              <AbsenceState kind="empty" what={`Ninguna regla coincide con “${search}”.`} />
            )}
          </div>
          <ul className="rule-list">
            {visibleRules.map(rule => (
              <li key={rule.ruleId}>
                <RuleRow rule={rule} />
              </li>
            ))}
          </ul>
        </section>

        <aside className="alerts__now">
          <div className="alerts__now-head-block">
            <h2>Ahora mismo</h2>
            <p>Las alertas que se han disparado y merecen tu atención.</p>
            <p className="alerts__now-live">
              <span aria-hidden="true" /> Monitoreo en vivo
            </p>
          </div>

          <div className="alerts__now-recent-head">
            <div>
              <span>Últimos disparos</span>
              <h3>Actividad reciente</h3>
            </div>
            <Link to="/alertas/historial">Ver todo →</Link>
          </div>

          {(events.isPending || events.isError || events.data?.events.length === 0) && (
            <div className="alerts__panel-pad">
              {events.isPending && <Skeleton variant="rows" count={3} />}
              {events.isError && <LoadFailure title="No se pudo cargar el historial" error={events.error} onRetry={events.refetch} />}
              {events.data && events.data.events.length === 0 && <AbsenceState kind="empty" what="Ninguna regla se ha cumplido todavía." />}
            </div>
          )}
          {events.data && events.data.events.length > 0 && (
            <ol className="alerts__now-list">
              {events.data.events.slice(0, RECENT_COUNT).map(event => (
                <li key={event.eventId}>
                  <RecentAlert event={event} unseen={event.occurredAt !== null && Date.parse(event.occurredAt) > seenAt} />
                </li>
              ))}
            </ol>
          )}
        </aside>
      </div>
    </div>
  );
}

/** Two digits reads as a figure worth a glance; one reads as an afterthought — the stat cards'
 *  whole point. Real counts can still run past 99 without breaking, they just stop padding. */
function twoDigits(value: number): string {
  return String(value).padStart(2, '0');
}

/** Comma-free substring match on name or keywords — the list is short, so this runs on every keystroke. */
function filterRules(rules: Array<EditorialAlertRule>, search: string): Array<EditorialAlertRule> {
  const query = search.trim().toLowerCase();
  if (query === '') return rules;
  return rules.filter(rule => rule.name.toLowerCase().includes(query) || rule.keywords.some(keyword => keyword.toLowerCase().includes(query)));
}

/** What an editor reads before deciding whether to open this screen further. Three real counts —
 *  there is no fourth "priority" figure because nothing in the data says what that would mean.
 *  Boxed cards rather than the bare hairline band a continuous strip of counts wears: that band
 *  reads as one continuous strip, which is right for six related counts and wrong for three cards
 *  an editor scans independently. */
function AlertStats({ figures }: { figures: AlertFigures }) {
  return (
    <div className="alert-stats">
      <article className="alert-stats__card">
        <dt>Reglas armadas</dt>
        <dd data-tone={figures.activeRules > 0 ? 'supported' : undefined}>{twoDigits(figures.activeRules)}</dd>
      </article>
      <article className="alert-stats__card">
        <dt>Coincidencias hoy</dt>
        <dd data-tone={figures.eventsToday > 0 ? 'supported' : undefined}>{twoDigits(figures.eventsToday)}</dd>
        {figures.eventsUnseen > 0 && <small data-tone="contradicted">{figures.eventsUnseen} por revisar</small>}
      </article>
      <article className="alert-stats__card">
        <dt>Avisos por correo con problema</dt>
        <dd data-tone={figures.mailIssues > 0 ? 'contradicted' : undefined}>{twoDigits(figures.mailIssues)}</dd>
        <small>de lo cargado más reciente</small>
      </article>
    </div>
  );
}

/** Deliberately not the full `AlertEntry` (clock offset, keywords, excerpt, mail status) — this is
 *  a glance row, three lines, not a second copy of the history page. */
function RecentAlert({ event, unseen }: { event: EditorialAlertEvent; unseen: boolean }) {
  return (
    <article className="alerts__now-item">
      <i className={`alerts__now-dot${unseen ? ' alerts__now-dot--unseen' : ''}`} aria-hidden="true" />
      <div>
        <strong>{event.ruleName || event.ruleId}</strong>
        <p>
          {ALERT_SURFACE_LABEL[event.surface]} · {event.channelName || event.streamId}
        </p>
        <div className="alerts__now-meta">
          <time>{relativeDayTime(event.occurredAt, new Date())}</time>
          <Badge tone={unseen ? 'contradicted' : 'supported'}>{unseen ? 'REVISAR' : 'VISTO'}</Badge>
        </div>
      </div>
    </article>
  );
}

function RuleRow({ rule }: { rule: EditorialAlertRule }) {
  const navigate = useNavigate();
  const update = useUpdateAlertRule();
  const remove = useDeleteAlertRule();
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const tone = ruleMarkTone(rule.surfaces);

  return (
    <article className="rule" data-active={rule.active}>
      <header className="rule__head">
        <div className="rule__mark" data-tone={tone}>
          <Icon name={ruleMarkIcon(tone)} />
        </div>
        <div className="rule__intro">
          <span className="rule__name">{rule.name}</span>
          <span className="rule__match mono">{rule.matchAll ? 'todas las palabras' : 'cualquier palabra'}</span>
          <ul className="topic-tags topic-tags--small">
            {rule.keywords.map(keyword => (
              <li key={keyword} className="topic-tag">
                {keyword}
              </li>
            ))}
          </ul>
        </div>
        <Badge tone={rule.active ? 'supported' : 'unresolved'}>{rule.active ? 'activa' : 'pausada'}</Badge>
      </header>

      <div className="signal-grid">
        <div>
          <small>Palabras</small>
          <strong>
            {rule.keywords.length} término{rule.keywords.length === 1 ? '' : 's'}
          </strong>
        </div>
        <div>
          <small>Dónde busca</small>
          <strong>{formatAlertScopes(rule.surfaces) || 'ningún ámbito'}</strong>
        </div>
        <div>
          <small>Silencio</small>
          <strong>{rule.cooldownMinutes} min</strong>
        </div>
      </div>

      <p className="rule__meta">
        {rule.channelIds.length === 0 ? 'todos los canales' : `${rule.channelIds.length} canal(es)`}
        {rule.surfaces.includes('relevance') && rule.minRelevanceScore !== null && ` · relevancia desde ${rule.minRelevanceScore}`}
        {rule.notifyEmails.length > 0 && ` · correo a ${rule.notifyEmails.join(', ')}`}
        {rule.createdBy && ` · creada por ${rule.createdBy}`}
        {rule.createdAt && ` · ${formatDateTime(rule.createdAt)}`}
      </p>

      <footer className="rule__actions">
        <span className="rule__monitoring">{rule.active ? 'Monitoreando' : 'En pausa'}</span>
        {confirmingDelete ? (
          <div className="rule__actions-buttons">
            {/* Deleting keeps the alerts it already raised — they carry the rule's name, which is
                what lets the historial keep explaining itself. */}
            <span className="rule__confirm">Se borra la regla; su historial se conserva.</span>
            <Button variant="danger" busy={remove.isPending} onClick={() => remove.mutate(rule.ruleId)}>
              Borrar
            </Button>
            <Button variant="ghost" onClick={() => setConfirmingDelete(false)}>
              Cancelar
            </Button>
          </div>
        ) : (
          <RuleMenu
            ruleName={rule.name}
            active={rule.active}
            busy={update.isPending}
            onEdit={() => navigate(`/alertas/${encodeURIComponent(rule.ruleId)}/editar`)}
            onToggle={() => update.mutate({ ruleId: rule.ruleId, active: !rule.active })}
            onDelete={() => setConfirmingDelete(true)}
          />
        )}
      </footer>

      {(update.isError || remove.isError) && <Notice tone="contradicted" title="No se pudo aplicar el cambio" role="alert" />}
    </article>
  );
}

/**
 * Editar, Pausar/Reanudar, and Borrar — the reference design only draws the first two (deleting
 * isn't a feature it has), but folding this product's real delete into the same menu keeps every
 * row's baseline footprint identical to the reference's: one status line, one kebab. Deleting only
 * opens `RuleRow`'s confirm step; it never fires from inside the menu itself. Closes on an outside
 * click or Escape, and moves focus to its first item on open — the same contract the reference
 * implementation used, because both are real accessibility requirements, not decoration: a menu
 * that traps neither mouse nor keyboard focus is one half of a menu.
 */
function RuleMenu({
  ruleName,
  active,
  busy,
  onEdit,
  onToggle,
  onDelete,
}: {
  ruleName: string;
  active: boolean;
  busy: boolean;
  onEdit: () => void;
  onToggle: () => void;
  onDelete: () => void;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const firstItemRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    firstItemRef.current?.focus();

    function onPointerDown(event: PointerEvent) {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div className="rule-menu" ref={rootRef}>
      <button
        type="button"
        className="rule-menu__trigger"
        aria-label={`Más acciones para ${ruleName}`}
        aria-expanded={open}
        onClick={() => setOpen(value => !value)}
      >
        ⋮
      </button>
      {open && (
        <div className="rule-menu__list" role="menu">
          <button
            ref={firstItemRef}
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              onEdit();
            }}
          >
            Editar
          </button>
          <button
            type="button"
            role="menuitem"
            disabled={busy}
            onClick={() => {
              setOpen(false);
              onToggle();
            }}
          >
            {active ? 'Pausar alerta' : 'Reanudar alerta'}
          </button>
          <button
            type="button"
            role="menuitem"
            className="rule-menu__delete"
            onClick={() => {
              setOpen(false);
              onDelete();
            }}
          >
            Borrar
          </button>
        </div>
      )}
    </div>
  );
}
